"use client";

/**
 * SortableTable — wrapper padrão do app sobre TanStack Table + shadcn classes.
 *
 * Convenções:
 *   - Header com chevron 3-state (neutro | ↑ | ↓); click toggla sort.
 *   - Sort persistido em URL param (?sort=key&dir=asc) para shareability.
 *   - Filtro de texto na barra superior (#6) — case/accent-insensitive sobre
 *     os valores das colunas visíveis (cru + formatado). Persistido em URL
 *     (?q= / ?{prefix}_q=) com debounce 300ms; filtragem em si é instantânea
 *     (estado local). Opt-out via `filterable={false}`.
 *   - Células passam pelo `format` da coluna; `TRAVESSAO` quando null/undefined.
 *   - Tipos suportados: "number" (numeric compare), "text" (locale-aware), "date" (ISO string lex).
 *
 * Uso típico:
 *   <SortableTable
 *     data={rows}
 *     columns={[
 *       { key: "nome", header: "Nome", sort: "text" },
 *       { key: "vdma", header: "VDMA (veh/dia)", sort: "number", format: formatVolume },
 *     ]}
 *     defaultSort={{ key: "nome", dir: "asc" }}
 *     urlParamPrefix="projetos"  // ?projetos_sort=nome&projetos_dir=asc
 *   />
 */

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { ChevronDown, ChevronUp, ChevronsUpDown, Search, X } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";

import { TRAVESSAO } from "@/lib/formatters";

import { filterRows } from "./table-filter";

export type SortType = "text" | "number" | "date";
export type SortDir = "asc" | "desc";

export interface SortableColumn<T> {
  /** Chave de identificação da coluna + field path default para acessor. */
  key: string;
  /** Header renderizado na tabela. */
  header: string;
  /** Tipo de ordenação (gera sortingFn apropriado). */
  sort?: SortType | false;
  /** Acessor customizado (default: row[key]). */
  accessor?: (row: T) => unknown;
  /** Formatter aplicado ao valor para render. Default: String(value) / TRAVESSAO. */
  format?: (value: unknown, row: T) => React.ReactNode;
  /** Alinhamento da célula. */
  align?: "left" | "right" | "center";
  /** Class extra na célula <td>. */
  cellClassName?: string;
  /** Class extra no header <th>. */
  headerClassName?: string;
  /**
   * Phase A.3 — pin column sticky-left no scroll horizontal. Aplica em <th> + <td>.
   * Valor = offset px from left edge (primeira sticky = 0; segundas somam widths).
   * Last sticky col ganha shadow direita pra marcar boundary com cols rolagem.
   * Padrão VDMA WIDE Resultados (sticky left-0 / left-[Npx] z-10 bg-tide-bg-secondary).
   */
  stickyLeft?: number;
  /**
   * Hidden accessor column — registered in TanStack for sorting purposes but
   * excluded from header and body rendering. Use when defaultSortKey must
   * reference a numeric field (e.g. los_severity) that has no visible column.
   */
  hidden?: boolean;
}

export interface SortableTableProps<T> {
  data: T[];
  columns: SortableColumn<T>[];
  defaultSort?: { key: string; dir: SortDir };
  /** Prefixo de URL param (default: "sort"/"dir"). Útil para múltiplas tabelas na mesma rota. */
  urlParamPrefix?: string;
  /** Row key extractor (default: index). */
  getRowId?: (row: T, index: number) => string;
  /** Click na linha. */
  onRowClick?: (row: T) => void;
  /** Mensagem de empty state. */
  emptyMessage?: string;
  /** Class extra no <table>. */
  className?: string;
  /**
   * Optional per-row class. Returned string is appended to each row's <tr>
   * className. Used by NsResultTable to tint los='F' rows (CONTEXT.md §LOS
   * row coloring). When undefined, no per-row class is applied.
   */
  rowClassName?: (row: T) => string | undefined;
  /**
   * Filtro de texto na barra superior (#6). Default: true. `false` esconde a
   * barra (tabelas onde filtro não faz sentido — ex. matriz fixa pequena).
   */
  filterable?: boolean;
  /** Placeholder do input de filtro (default: "Filtrar…"). */
  filterPlaceholder?: string;
}

function SortIcon({ sorted }: { sorted: false | SortDir }) {
  if (sorted === "asc") return <ChevronUp className="h-3 w-3" aria-label="Ordenado ascendente" />;
  if (sorted === "desc") return <ChevronDown className="h-3 w-3" aria-label="Ordenado descendente" />;
  return <ChevronsUpDown className="h-3 w-3 opacity-30" aria-label="Ordenar" />;
}

function renderValue<T>(col: SortableColumn<T>, value: unknown, row: T): React.ReactNode {
  if (col.format) {
    const out = col.format(value, row);
    return out === null || out === undefined || out === "" ? TRAVESSAO : out;
  }
  if (value === null || value === undefined || value === "") return TRAVESSAO;
  return String(value);
}

export function SortableTable<T>({
  data,
  columns,
  defaultSort,
  urlParamPrefix,
  getRowId,
  onRowClick,
  emptyMessage = "Nenhum registro encontrado.",
  className,
  rowClassName,
  filterable = true,
  filterPlaceholder = "Filtrar…",
}: SortableTableProps<T>) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const sortKey = urlParamPrefix ? `${urlParamPrefix}_sort` : "sort";
  const dirKey = urlParamPrefix ? `${urlParamPrefix}_dir` : "dir";
  const qKey = urlParamPrefix ? `${urlParamPrefix}_q` : "q";

  // Filtro (#6): estado local dirige a filtragem (instantâneo); URL é
  // shareability — init do param e sync com debounce 300ms (padrão
  // admin/contagens), evitando um router.replace por keystroke.
  const [query, setQuery] = useState(() => searchParams.get(qKey) ?? "");
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(query), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    const current = searchParams.get(qKey) ?? "";
    if (current === debouncedQuery) return;
    const params = new URLSearchParams(searchParams.toString());
    if (debouncedQuery) {
      params.set(qKey, debouncedQuery);
    } else {
      params.delete(qKey);
    }
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [debouncedQuery, qKey, pathname, router, searchParams]);

  const filteredData = useMemo(
    () => (filterable ? filterRows(data, columns, query) : data),
    [filterable, data, columns, query],
  );

  const urlSort = searchParams.get(sortKey);
  const urlDir = searchParams.get(dirKey) as SortDir | null;

  const sorting: SortingState = useMemo(() => {
    const k = urlSort ?? defaultSort?.key;
    const d = urlDir ?? defaultSort?.dir ?? "asc";
    if (!k) return [];
    return [{ id: k, desc: d === "desc" }];
  }, [urlSort, urlDir, defaultSort?.key, defaultSort?.dir]);

  const setSorting = (updater: SortingState | ((old: SortingState) => SortingState)) => {
    const next =
      typeof updater === "function" ? (updater as (s: SortingState) => SortingState)(sorting) : updater;
    const params = new URLSearchParams(searchParams.toString());
    if (next.length === 0) {
      params.delete(sortKey);
      params.delete(dirKey);
    } else {
      params.set(sortKey, next[0].id);
      params.set(dirKey, next[0].desc ? "desc" : "asc");
    }
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  };

  const tanColumns: ColumnDef<T>[] = useMemo(
    () =>
      columns.map((col) => {
        const accessor = col.accessor ?? ((row: T) => (row as Record<string, unknown>)[col.key]);
        const def: ColumnDef<T> = {
          id: col.key,
          accessorFn: accessor as (row: T) => unknown,
          header: col.header,
          enableSorting: col.sort !== false,
          cell: ({ getValue, row }) => renderValue(col, getValue(), row.original),
        };
        if (col.sort === "number") {
          def.sortingFn = (a, b, id) => {
            const va = a.getValue(id);
            const vb = b.getValue(id);
            const na = typeof va === "number" ? va : va == null ? Number.NEGATIVE_INFINITY : Number(va);
            const nb = typeof vb === "number" ? vb : vb == null ? Number.NEGATIVE_INFINITY : Number(vb);
            return na - nb;
          };
        } else if (col.sort === "date") {
          def.sortingFn = (a, b, id) => {
            const va = String(a.getValue(id) ?? "");
            const vb = String(b.getValue(id) ?? "");
            return va.localeCompare(vb);
          };
        } else if (col.sort === "text") {
          def.sortingFn = (a, b, id) => {
            const va = String(a.getValue(id) ?? "");
            const vb = String(b.getValue(id) ?? "");
            return va.localeCompare(vb, "pt-BR", { sensitivity: "base" });
          };
        }
        return def;
      }),
    [columns],
  );

  // Derive columnVisibility from hidden flag so TanStack's getVisibleCells /
  // getHeaderGroups naturally skip hidden accessor columns while still
  // registering them for sort (fixes "Column with id X does not exist" when
  // defaultSort.key references a numeric field without a visible column).
  const columnVisibility: VisibilityState = useMemo(
    () =>
      Object.fromEntries(
        columns.filter((c) => c.hidden).map((c) => [c.key, false]),
      ),
    [columns],
  );

  const table = useReactTable({
    data: filteredData,
    columns: tanColumns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: getRowId ? (row, idx) => getRowId(row, idx) : undefined,
  });

  const alignClass = (a?: "left" | "right" | "center") =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  // Phase A.3 — sticky-left CSS builder. Identifica última col sticky por key
  // pra aplicar shadow apenas no boundary com cols roláveis. Offset px via
  // inline style (Tailwind JIT não processa template literals dinâmicos).
  const stickyCols = columns.filter((c) => c.stickyLeft !== undefined);
  const lastStickyKey = stickyCols.length
    ? stickyCols[stickyCols.length - 1].key
    : null;
  const stickyClass = (col: SortableColumn<T> | undefined): string => {
    if (!col || col.stickyLeft === undefined) return "";
    const shadow =
      col.key === lastStickyKey
        ? " shadow-[2px_0_4px_rgba(0,0,0,0.08)]"
        : "";
    return ` sticky bg-tide-bg-secondary z-10${shadow}`;
  };
  const stickyStyle = (
    col: SortableColumn<T> | undefined,
  ): React.CSSProperties | undefined =>
    col?.stickyLeft !== undefined ? { left: `${col.stickyLeft}px` } : undefined;

  const showFilterBar = filterable && data.length > 0;

  return (
    <div className={`overflow-hidden rounded-lg border border-tide-border bg-tide-bg-secondary ${className ?? ""}`}>
      {showFilterBar && (
        <div className="flex items-center gap-2 border-b border-tide-border px-3 py-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-tide-text-muted" aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={filterPlaceholder}
            aria-label="Filtrar tabela"
            className="w-56 max-w-full bg-transparent text-xs text-tide-text-primary placeholder:text-tide-text-muted focus:outline-none"
          />
          {query !== "" && (
            <>
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Limpar filtro"
                className="rounded p-0.5 text-tide-text-muted hover:text-tide-text-primary"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <span className="ml-auto whitespace-nowrap text-[11px] text-tide-text-muted">
                {filteredData.length} de {data.length}
              </span>
            </>
          )}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
        <thead className="border-b border-tide-border text-tide-text-muted">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => {
                const col = columns.find((c) => c.key === h.column.id);
                const canSort = h.column.getCanSort();
                const sorted = h.column.getIsSorted();
                return (
                  <th
                    key={h.id}
                    scope="col"
                    className={`px-3 py-2 font-semibold ${alignClass(col?.align)} ${canSort ? "cursor-pointer select-none hover:text-tide-text-primary" : ""}${stickyClass(col)} ${col?.headerClassName ?? ""}`}
                    style={stickyStyle(col)}
                    onClick={canSort ? h.column.getToggleSortingHandler() : undefined}
                    aria-sort={
                      sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : canSort ? "none" : undefined
                    }
                  >
                    <div className={`flex items-center gap-1 ${col?.align === "right" ? "justify-end" : col?.align === "center" ? "justify-center" : ""}`}>
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {canSort && <SortIcon sorted={sorted} />}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className="text-tide-text-secondary">
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.filter((c) => !c.hidden).length} className="px-3 py-8 text-center text-tide-text-muted">
                {data.length > 0 && query.trim() !== ""
                  ? `Nenhum resultado para "${query.trim()}".`
                  : emptyMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => {
              const extraRowClass = rowClassName?.(row.original) ?? "";
              return (
                <tr
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={`border-b border-tide-border/50 hover:bg-tide-bg-hover ${onRowClick ? "cursor-pointer" : ""} ${extraRowClass}`}
                >
                  {row.getVisibleCells().map((cell) => {
                    const col = columns.find((c) => c.key === cell.column.id);
                    return (
                      <td
                        key={cell.id}
                        className={`px-3 py-1.5 ${alignClass(col?.align)}${stickyClass(col)} ${col?.cellClassName ?? ""}`}
                        style={stickyStyle(col)}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
        </table>
      </div>
    </div>
  );
}
